/**
 * 재처리 결과 검토 모달.
 *
 * UX 흐름:
 *   1. 사용자가 RecordTable 의 "검토" 클릭 → 이 패널이 열린다.
 *   2. GET /record/diff 로 현재값 vs 신규(pending) 비교를 받는다.
 *   3. 사용자는 필드별 라디오(use_current / use_pending) 로 채택을 결정한다.
 *      - 기본값은 "변경된 필드 → use_pending", "변경 없는 필드 → use_current".
 *   4. 반려 상태/amount_analysis 도 별도 토글로 채택.
 *   5. "확정 채택" 으로 POST /apply 호출 → pending 비워지고 Job 캐시 무효화.
 *   6. 또는 "폐기" 로 DELETE /pending 호출.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, ArrowRight, Loader2, Sparkles, X } from "lucide-react";
import { Trans, useTranslation } from "react-i18next";

import { ApiError } from "@/lib/api/client";
import type { CorrectionStatus } from "@/lib/api/corrections";
import type { FieldBbox, InvoiceFileOut } from "@/lib/api/jobs";
import { cn } from "@/lib/cn";
import type {
  ApplyReextractionPayload,
  DiffFieldRow,
  FieldChoice,
  RejectionChoice,
} from "@/lib/api/records";

import { localizeAiText } from "./aiText";
import { FIELD_META_MAP, type EditableFieldKey } from "./fieldLabels";
import PdfPreviewPanel from "./PdfPreviewPanel";
import { useApplyReextraction, useDiscardPending } from "./useApplyReextraction";
import { useRecordDiff } from "./useRecordDiff";

/**
 * record diff 컬럼명을 현재 언어 라벨로 반환.
 *
 * `row.field` 는 ``EditableFieldKey`` 일 것으로 기대되지만, 백엔드에서 신규 컬럼이
 * 추가됐는데 프론트에 아직 메타 정의가 없는 경우에도 깨지지 않도록 raw key 를
 * fallback 으로 반환한다.
 */
function useFieldLabel(): (field: string) => string {
  const { t } = useTranslation();
  return (field: string) => {
    const meta = (FIELD_META_MAP as Record<string, { labelI18nKey: string } | undefined>)[
      field as EditableFieldKey
    ];
    return meta ? t(meta.labelI18nKey) : field;
  };
}

interface Props {
  jobId: string;
  file: InvoiceFileOut;
  onClose: () => void;
  /**
   * 반영(apply) 성공 직후 콜백. 지정 시 onClose 대신 호출된다.
   * (목록에서 검토로 진입한 경우, 반영 후 케이스 편집 화면으로 전환하기 위함)
   */
  onApplied?: () => void;
  /** #4 — 수정제안 재추출이 진행 중(결과 미도착). true 면 스켈레톤(시머)로 대기. */
  inFlight?: boolean;
  /** #4 — 재추출 종료 상태. 변경없음/실패면 결과 대신 안내를 보여준다. */
  terminalStatus?: CorrectionStatus | null;
  /**
   * 최대화 여부. EditRecordModal 과 동일 크기 로직 공유용.
   * 미지정 시 편집창 저장값(``editRecordModal:maximized``, 기본 ON)을 따른다.
   */
  maximized?: boolean;
}

/** 편집창 최대화 선호값 저장 키 (EditRecordModal 과 동일). */
const MAXIMIZE_STORAGE_KEY = "editRecordModal:maximized";

function toStr(v: unknown): string {
  if (v == null || v === "") return "—";
  return String(v);
}

export default function DiffReviewPanel({
  jobId,
  file,
  onClose,
  onApplied,
  inFlight = false,
  terminalStatus = null,
  maximized,
}: Props) {
  const { t, i18n } = useTranslation();
  const fieldLabel = useFieldLabel();
  // 편집창과 동일한 최대화 크기를 공유. prop 우선, 없으면 저장값(기본 ON).
  const maxed = useMemo(() => {
    if (typeof maximized === "boolean") return maximized;
    try {
      return localStorage.getItem(MAXIMIZE_STORAGE_KEY) !== "0";
    } catch {
      return true;
    }
  }, [maximized]);
  const pendingPresent = file.record?.pending_extraction != null;
  // #4 — 결과 미도착 상태 분기: 진행중(시머) / 변경없음·실패(안내).
  const waitingForResult = !pendingPresent && (inFlight || terminalStatus == null);
  const noResultStatus =
    !pendingPresent && (terminalStatus === "failed" || terminalStatus === "completed_no_change")
      ? terminalStatus
      : null;
  const diffQ = useRecordDiff({
    jobId,
    fileId: file.id,
    // pending 이 사라진 직후엔 backend 가 409 를 줄 수 있으므로,
    // pending_extraction 이 있을 때만 enable.
    enabled: pendingPresent,
  });

  // 필드별 채택 — key → "use_pending" | "use_current"
  const [choices, setChoices] = useState<Record<string, FieldChoice>>({});
  const [applyRejection, setApplyRejection] = useState<RejectionChoice>("skip");
  const [applyAmount, setApplyAmount] = useState<boolean>(false);
  const [memo, setMemo] = useState<string>("");

  // diff 가 도착하면 기본 선택값을 설정 (변경된 필드는 use_pending).
  useEffect(() => {
    if (!diffQ.data) return;
    const defaults: Record<string, FieldChoice> = {};
    for (const row of diffQ.data.csv_fields) {
      defaults[row.field] = row.changed ? "use_pending" : "use_current";
    }
    setChoices(defaults);
    // 반려 변경이 있으면 use_pending, 아니면 skip
    setApplyRejection(diffQ.data.rejection.changed ? "use_pending" : "skip");
  }, [diffQ.data]);

  // ESC 닫기
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const applyMutation = useApplyReextraction();
  const discardMutation = useDiscardPending();

  const changedFields = useMemo<DiffFieldRow[]>(
    () => (diffQ.data?.csv_fields ?? []).filter((r) => r.changed),
    [diffQ.data],
  );
  const unchangedFields = useMemo<DiffFieldRow[]>(
    () => (diffQ.data?.csv_fields ?? []).filter((r) => !r.changed),
    [diffQ.data],
  );

  // changed 필드 중 use_pending 선택 개수 — 액션 버튼에 표시
  const adoptedCount = changedFields.filter((r) => choices[r.field] === "use_pending").length;

  const handleApply = () => {
    if (!diffQ.data) return;
    const payload: ApplyReextractionPayload = {
      field_choices: choices,
      apply_rejection: applyRejection,
      apply_amount_analysis: applyAmount,
    };
    if (memo.trim()) payload.memo = memo.trim();
    applyMutation.mutate(
      { jobId, fileId: file.id, payload },
      { onSuccess: () => (onApplied ?? onClose)() },
    );
  };

  const handleDiscard = () => {
    if (!window.confirm(t("records.diff.actions.discardConfirm"))) {
      return;
    }
    discardMutation.mutate({ jobId, fileId: file.id }, { onSuccess: () => onClose() });
  };

  const bulkSelect = (val: FieldChoice) => {
    if (!diffQ.data) return;
    setChoices(
      Object.fromEntries(diffQ.data.csv_fields.map((r) => [r.field, val])) as Record<
        string,
        FieldChoice
      >,
    );
  };

  const error = applyMutation.error ?? discardMutation.error ?? diffQ.error;
  const errorMsg = error instanceof ApiError ? error.detail : (error as Error | null)?.message;

  const busy = applyMutation.isPending || discardMutation.isPending;

  // 최신 handleApply 클로저를 ref 로 참조 — 매 렌더 재생성되는 함수를 effect
  // 의존성에 넣지 않아 keydown 리스너 재등록 없이 항상 최신 로직을 호출한다.
  const handleApplyRef = useRef(handleApply);
  handleApplyRef.current = handleApply;

  // Enter: "선택대로 확정"(채택) 실행. 메모 textarea 에서는 Shift+Enter 로 줄바꿈.
  //  - diff 결과가 준비되고(pending) 진행 중이 아닐 때만 발동.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Enter" || e.shiftKey || e.ctrlKey || e.metaKey || e.altKey) return;
      if (!pendingPresent || !diffQ.data || busy) return;
      e.preventDefault();
      handleApplyRef.current();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pendingPresent, diffQ.data, busy]);

  return (
    <div
      className={cn(
        "fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50",
        maxed ? "p-2" : "p-4",
      )}
      onClick={onClose}
    >
      <div
        className={cn(
          "flex w-full flex-col overflow-hidden rounded-lg bg-white shadow-xl",
          maxed ? "h-[calc(100vh-1rem)] max-w-none" : "h-[90vh] max-w-6xl",
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* 헤더 */}
        <header className="flex items-start justify-between border-b border-slate-200 px-5 py-3">
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-slate-900">{t("records.diff.title")}</h2>
            <p className="mt-0.5 truncate text-xs text-slate-500" title={file.original_name}>
              {file.original_name}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
            aria-label={t("common.actions.close")}
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        {noResultStatus ? (
          // #4 — 변경없음 / 실패: 결과 대신 안내(오버레이 안에서 처리).
          <div
            className={cn(
              "m-5 flex items-start justify-between gap-2 rounded-md border px-4 py-3 text-sm",
              noResultStatus === "failed"
                ? "border-red-200 bg-red-50 text-red-700"
                : "border-slate-200 bg-slate-50 text-slate-600",
            )}
          >
            <span>
              {noResultStatus === "failed"
                ? t("records.edit.correcting.failed")
                : t("records.edit.correcting.noChange")}
            </span>
            <button
              type="button"
              onClick={onClose}
              className="flex-shrink-0 rounded p-0.5 hover:bg-black/5"
              aria-label={t("common.actions.close")}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        ) : waitingForResult ? (
          // #4 — 결과 대기: 스켈레톤 + 시머(pulse). 도착 시 자동으로 diff 로 전환.
          <div className="flex min-h-0 flex-1 flex-col gap-3 p-5">
            <div className="flex items-center gap-2 text-sm text-violet-700">
              <Sparkles className="h-4 w-4 animate-pulse" />
              {t("records.edit.correcting.title")}
            </div>
            <p className="text-xs text-slate-500">{t("records.edit.correcting.body")}</p>
            <div className="mt-2 space-y-2.5">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="h-3 w-28 shrink-0 animate-pulse rounded bg-slate-200" />
                  <div className="h-8 flex-1 animate-pulse rounded bg-slate-100" />
                </div>
              ))}
            </div>
          </div>
        ) : diffQ.isLoading ? (
          <div className="flex items-center justify-center gap-2 py-10 text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            {t("records.diff.loading")}
          </div>
        ) : diffQ.isError || !diffQ.data ? (
          <div className="m-5 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <span>{errorMsg ?? t("records.diff.errorFallback")}</span>
          </div>
        ) : (
          <>
            {/* 본문 2단: 좌 PDF 미리보기 / 우 diff (검토 시 실제 문서 대조) */}
            <div className="flex min-h-0 flex-1 overflow-hidden">
              {/* 좌: PDF (넓은 화면에서만) */}
              <div className="hidden w-1/2 border-r border-slate-200 lg:block">
                <PdfPreviewPanel
                  jobId={jobId}
                  fileId={file.id}
                  originalName={file.original_name}
                  bboxes={(diffQ.data.csv_bbox as Record<string, FieldBbox | null> | null) ?? null}
                />
              </div>

              {/* 우: diff 내용 (독립 스크롤) */}
              <div className="flex min-w-0 flex-1 flex-col overflow-y-auto">
                {/* 메타 */}
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-b border-slate-100 bg-slate-50 px-5 py-2 text-[11px] text-slate-500">
                  <span>
                    {t("records.diff.meta.extractedAt")}{" "}
                    <span className="text-slate-700">
                      {diffQ.data.extracted_at
                        ? new Date(diffQ.data.extracted_at).toLocaleString()
                        : "—"}
                    </span>
                  </span>
                  <span>
                    {t("records.diff.meta.triggeredByUser")}
                    <span className="text-slate-700">{diffQ.data.triggered_by_user_id ?? "?"}</span>
                  </span>
                  <span
                    className={cn(
                      "rounded px-1.5 py-0.5",
                      diffQ.data.success
                        ? "bg-green-100 text-green-700"
                        : "bg-red-100 text-red-700",
                    )}
                  >
                    {diffQ.data.success
                      ? t("records.diff.meta.success")
                      : t("records.diff.meta.failed")}
                  </span>
                  {diffQ.data.error_message && (
                    <span className="text-red-600">{diffQ.data.error_message}</span>
                  )}
                </div>

                {/* M15 — 수정제안 결과인 경우 사용자 피드백/근거 배너 */}
                {diffQ.data.source === "correction" && diffQ.data.correction && (
                  <div className="mx-5 mt-3 rounded-md border border-violet-200 bg-violet-50 px-3 py-2 text-xs">
                    <div className="flex items-center gap-1.5 font-medium text-violet-800">
                      <Sparkles className="h-3.5 w-3.5" />
                      {t("records.diff.correction.banner", {
                        count: diffQ.data.correction.annotation_count,
                      })}
                    </div>
                    <p className="mt-1 whitespace-pre-wrap text-slate-700">
                      “{diffQ.data.correction.feedback}”
                    </p>
                    {diffQ.data.correction.reasoning && (
                      <details className="mt-1.5">
                        <summary className="cursor-pointer text-violet-700">
                          {t("records.diff.correction.reasoning")}
                        </summary>
                        <p className="mt-1 whitespace-pre-wrap text-slate-600">
                          {localizeAiText(diffQ.data.correction.reasoning, i18n.language)}
                        </p>
                      </details>
                    )}
                  </div>
                )}

                {/* 일괄 액션 */}
                <div className="flex items-center justify-between border-b border-slate-100 px-5 py-2">
                  {/*
                   * <Trans> — ``records.diff.summary`` 키에 <strong> 마크업이
                   * 두 군데 포함되어 있다. ``components`` 의 ``strong`` 매핑은
                   * 두 곳에 동일하게 적용되지만, i18next 가 같은 태그명을 인덱
                   * 스 기반으로 처리하므로 별도 인덱싱 없이 OK.
                   */}
                  <p className="text-xs text-slate-500">
                    <Trans
                      i18nKey="records.diff.summary"
                      values={{ changed: changedFields.length, adopted: adoptedCount }}
                      components={{
                        strong: <span className="font-medium text-slate-700" />,
                      }}
                    />
                  </p>
                  <div className="flex gap-1.5">
                    <button
                      type="button"
                      onClick={() => bulkSelect("use_pending")}
                      className="rounded-md border border-blue-200 px-2 py-1 text-[11px] text-blue-700 transition hover:bg-blue-50"
                    >
                      {t("records.diff.bulk.allNew")}
                    </button>
                    <button
                      type="button"
                      onClick={() => bulkSelect("use_current")}
                      className="rounded-md border border-slate-200 px-2 py-1 text-[11px] text-slate-700 transition hover:bg-slate-50"
                    >
                      {t("records.diff.bulk.allCurrent")}
                    </button>
                  </div>
                </div>

                {/* 비교 테이블 */}
                <div className="px-5 py-3">
                  {changedFields.length === 0 ? (
                    <p className="py-6 text-center text-xs text-slate-400">
                      {t("records.diff.empty")}
                    </p>
                  ) : (
                    <ul className="space-y-1.5">
                      {changedFields.map((row) => (
                        <DiffRow
                          key={row.field}
                          row={row}
                          choice={choices[row.field] ?? "use_current"}
                          onChange={(val) => setChoices((p) => ({ ...p, [row.field]: val }))}
                        />
                      ))}
                    </ul>
                  )}

                  {/* 반려 상태 비교 */}
                  {diffQ.data.rejection.changed && (
                    <section className="mt-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3">
                      <h3 className="text-xs font-medium text-amber-800">
                        {t("records.diff.rejection.title")}
                      </h3>
                      <div className="mt-2 grid grid-cols-1 gap-2 text-xs sm:grid-cols-2">
                        <div>
                          <p className="text-[10px] uppercase text-slate-500">
                            {t("records.diff.rejection.current")}
                          </p>
                          <p>
                            {diffQ.data.rejection.current.is_rejected
                              ? t("records.diff.rejection.valueRejected")
                              : t("records.diff.rejection.valueOk")}
                          </p>
                          {diffQ.data.rejection.current.rejection_reason && (
                            <p className="text-slate-600">
                              {diffQ.data.rejection.current.rejection_reason}
                            </p>
                          )}
                        </div>
                        <div>
                          <p className="text-[10px] uppercase text-slate-500">
                            {t("records.diff.rejection.pending")}
                          </p>
                          <p>
                            {diffQ.data.rejection.pending.is_rejected
                              ? t("records.diff.rejection.valueRejected")
                              : t("records.diff.rejection.valueOk")}
                          </p>
                          {diffQ.data.rejection.pending.rejection_reason && (
                            <p className="text-slate-600">
                              {diffQ.data.rejection.pending.rejection_reason}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="mt-2 flex gap-3 text-xs">
                        {(["skip", "use_pending", "use_current"] as const).map((val) => (
                          <label key={val} className="inline-flex items-center gap-1">
                            <input
                              type="radio"
                              name="apply_rejection"
                              value={val}
                              checked={applyRejection === val}
                              onChange={() => setApplyRejection(val)}
                            />
                            {val === "skip"
                              ? t("records.diff.rejection.optionSkip")
                              : val === "use_pending"
                                ? t("records.diff.rejection.optionUsePending")
                                : t("records.diff.rejection.optionUseCurrent")}
                          </label>
                        ))}
                      </div>
                    </section>
                  )}

                  {/* amount_analysis 채택 */}
                  <section className="mt-3 rounded-md border border-slate-200 px-4 py-2">
                    <label className="flex items-center gap-2 text-xs text-slate-700">
                      <input
                        type="checkbox"
                        checked={applyAmount}
                        onChange={(e) => setApplyAmount(e.target.checked)}
                      />
                      {t("records.diff.amount.checkbox")}
                    </label>
                  </section>

                  {/* 변경 없는 필드 (참고용 접이식) */}
                  {unchangedFields.length > 0 && (
                    <details className="mt-3 rounded-md border border-slate-100 px-3 py-2 text-[11px] text-slate-500">
                      <summary className="cursor-pointer">
                        {t("records.diff.unchanged.summary", { count: unchangedFields.length })}
                      </summary>
                      <ul className="mt-2 grid grid-cols-2 gap-x-3 gap-y-0.5">
                        {unchangedFields.map((row) => (
                          <li key={row.field} className="flex items-baseline gap-1">
                            <span className="text-slate-400">{fieldLabel(row.field)}:</span>
                            <span className="truncate">{toStr(row.current)}</span>
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}
                </div>

                {/* 메모 */}
                <div className="border-t border-slate-100 px-5 py-3">
                  <label className="block text-xs text-slate-600">
                    <span>{t("records.diff.memo.label")}</span>
                    <input
                      type="text"
                      value={memo}
                      onChange={(e) => setMemo(e.target.value)}
                      maxLength={500}
                      placeholder={t("records.diff.memo.placeholder")}
                      className="mt-1 w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm text-slate-900 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-200"
                    />
                  </label>
                </div>

                {/* 오류 */}
                {errorMsg && (
                  <div className="mx-5 mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                    {errorMsg}
                  </div>
                )}
              </div>
              {/* /우: diff 내용 */}
            </div>
            {/* /본문 2단 */}

            {/* 버튼 */}
            <footer className="flex items-center justify-between border-t border-slate-200 bg-slate-50 px-5 py-3">
              <button
                type="button"
                onClick={handleDiscard}
                disabled={busy}
                className="rounded-md border border-red-200 bg-white px-3 py-1.5 text-xs text-red-700 transition hover:bg-red-50 disabled:opacity-50"
              >
                {discardMutation.isPending && (
                  <Loader2 className="mr-1 inline h-3 w-3 animate-spin" />
                )}
                {t("records.diff.actions.discard")}
              </button>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs text-slate-700 transition hover:bg-slate-100"
                >
                  {t("common.actions.close")}
                </button>
                <button
                  type="button"
                  onClick={handleApply}
                  disabled={busy}
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium text-white transition",
                    busy ? "cursor-not-allowed bg-slate-300" : "bg-blue-600 hover:bg-blue-700",
                  )}
                >
                  {applyMutation.isPending && <Loader2 className="h-3 w-3 animate-spin" />}
                  {t("records.diff.actions.apply")}
                  <kbd className="ml-0.5 rounded border border-white/40 px-1 py-0.5 text-[10px] font-normal opacity-80">
                    Enter
                  </kbd>
                </button>
              </div>
            </footer>
          </>
        )}
      </div>
    </div>
  );
}

function DiffRow({
  row,
  choice,
  onChange,
}: {
  row: DiffFieldRow;
  choice: FieldChoice;
  onChange: (val: FieldChoice) => void;
}) {
  const { t } = useTranslation();
  const fieldLabel = useFieldLabel();
  return (
    <li
      className={cn(
        "grid grid-cols-12 items-center gap-2 rounded-md border px-3 py-2 text-xs",
        choice === "use_pending" ? "border-blue-200 bg-blue-50/30" : "border-slate-200",
      )}
    >
      <div className="col-span-3 truncate text-slate-700" title={row.field}>
        {fieldLabel(row.field)}
      </div>
      <div className="col-span-3">
        <p className="text-[10px] uppercase text-slate-400">{t("records.diff.row.current")}</p>
        <p
          className={cn(
            "truncate",
            choice === "use_current"
              ? "font-medium text-slate-900"
              : "text-slate-500 line-through decoration-slate-300",
          )}
          title={toStr(row.current)}
        >
          {toStr(row.current)}
        </p>
      </div>
      <ArrowRight className="col-span-1 mx-auto h-3 w-3 text-slate-400" />
      <div className="col-span-3">
        <p className="text-[10px] uppercase text-slate-400">{t("records.diff.row.pending")}</p>
        <p
          className={cn(
            "truncate",
            choice === "use_pending"
              ? "font-medium text-blue-700"
              : "text-slate-500 line-through decoration-slate-300",
          )}
          title={toStr(row.pending)}
        >
          {toStr(row.pending)}
        </p>
      </div>
      <div className="col-span-2 flex flex-col gap-0.5 text-[11px]">
        <label className="inline-flex items-center gap-1">
          <input
            type="radio"
            checked={choice === "use_pending"}
            onChange={() => onChange("use_pending")}
          />
          {t("records.diff.row.radioPending")}
        </label>
        <label className="inline-flex items-center gap-1">
          <input
            type="radio"
            checked={choice === "use_current"}
            onChange={() => onChange("use_current")}
          />
          {t("records.diff.row.radioCurrent")}
        </label>
      </div>
    </li>
  );
}
